/**
 * Ask credits, on the Billing page: the balance, how to add to it, and the
 * history of money moving in and out.
 *
 * Mounted at the tab level, not inside BillingOverview: a FREE team renders
 * the pricing grid instead of that component, and is the team most likely to
 * want a top-up.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTeam } from 'shared/context/teamContext';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { TableFooter } from 'shared/components/tables';
import { settingsInputClass } from 'features/settings/components/settingsTheme';
import { getCredits, startCreditCheckout } from 'features/settings/api/creditsApi';
import {
  usd, LEDGER_LABEL, MONEY_KINDS, Section, Stat, btnPrimary, btnNeutral, linkBtn,
  tableShell, th, thRow, trBody, td, MUTED,
} from 'features/settings/components/panels/askCreditsShared';

const KIND_STATUS = { purchase: 'Paid', refund: 'Refunded', grant: 'Credited', expire: 'Lapsed' };
const PAGE = 8;

const day = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function AskCreditsCard() {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id || selectedTeam?._id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buying, setBuying] = useState(false);
  const [custom, setCustom] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [shown, setShown] = useState(PAGE);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getCredits(teamId));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not load credits');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const topUp = async (amountUsd) => {
    setBuying(true);
    setError(null);
    try {
      const out = await startCreditCheckout(teamId, amountUsd);
      if (out?.url) window.location.href = out.url;
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not start checkout');
      setBuying(false);
    }
    // No finally: on success the browser is leaving for Stripe.
  };

  const moneyRows = useMemo(
    () => (data?.ledger || []).filter((l) => MONEY_KINDS.has(l.kind)),
    [data],
  );

  if (!teamId) return null;

  const balance = data?.balance || {};
  const allowance = data?.allowance;
  const disabled = balance.credits_enabled === false;
  const min = data?.topup?.min_usd ?? 5;
  const max = data?.topup?.max_usd ?? 1000;
  const customAmount = Number(custom);
  const customValid = Number.isFinite(customAmount) && customAmount >= min && customAmount <= max;
  const visible = moneyRows.slice(0, shown);

  return (
    <Section
      title="Lucid credits"
      action={!loading ? <button type="button" className={linkBtn} onClick={load}>Refresh</button> : null}
    >
      {loading && !data ? (
        <BrandLoader size="md" label="Loading Lucid credits" />
      ) : error && !data ? (
        <div className="flex items-center gap-3 text-[12px]" style={{ color: MUTED }}>
          {error}
          <button type="button" className={linkBtn} onClick={load}>Retry</button>
        </div>
      ) : disabled ? (
        <div className="text-[12px]" style={{ color: MUTED }}>Lucid runs on your own model keys.</div>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-end gap-10">
            <Stat big label="Available" value={usd(balance.total_usd)} />
            <Stat
              label={allowance?.period_end
                ? `Plan credit, renews ${day(allowance.period_end)}`
                : 'Plan credit'}
              value={usd(balance.plan_usd)}
            />
            <Stat label="Purchased" value={usd(balance.purchased_usd)} />
          </div>

          <div className="mb-6 flex flex-wrap items-center gap-2">
            {(data?.topup?.presets_usd || [10, 25, 50, 100]).map((amt) => (
              <button key={amt} type="button" className={btnNeutral} disabled={buying} onClick={() => topUp(amt)}>
                ${amt}
              </button>
            ))}
            <button
              type="button"
              className={btnNeutral}
              disabled={buying}
              onClick={() => setShowCustom((v) => !v)}
              aria-expanded={showCustom}
            >
              Other
            </button>
            {showCustom ? (
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => { e.preventDefault(); if (customValid) topUp(customAmount); }}
              >
                <span className="text-[12px]" style={{ color: MUTED }}>$</span>
                <input
                  className={`${settingsInputClass} h-8 border-[color:var(--border-default)]`}
                  style={{ width: 96, backgroundColor: 'transparent' }}
                  type="number"
                  min={min}
                  max={max}
                  step="0.01"
                  inputMode="decimal"
                  value={custom}
                  autoFocus
                  placeholder={`${min}–${max}`}
                  aria-label="Credit amount in US dollars"
                  onChange={(e) => setCustom(e.target.value)}
                />
                <button type="submit" className={btnPrimary} disabled={buying || !customValid}>
                  Add credit
                </button>
              </form>
            ) : null}
          </div>

          {error ? <p className="mb-3 text-[12px]" style={{ color: 'var(--feedback-error)' }}>{error}</p> : null}

          <div className="mb-2 text-[12px] font-semibold" style={{ color: 'var(--text-primary)' }}>History</div>
          {moneyRows.length ? (
            <>
              <div className={tableShell}>
                <table className="w-full min-w-[480px] border-collapse">
                  <thead>
                    <tr className={thRow}>
                      <th className={th} style={{ width: 140 }}>Date</th>
                      <th className={th}>Description</th>
                      <th className={`${th} text-right`} style={{ width: 120 }}>Amount</th>
                      <th className={th} style={{ width: 100 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((l) => {
                      const amt = Number(l.amount_usd);
                      return (
                        <tr key={l.id} className={trBody}>
                          <td className={`${td} tabular-nums`}>{day(l.created_at)}</td>
                          <td className={td}>{LEDGER_LABEL[l.kind] || l.kind}</td>
                          <td className={`${td} text-right tabular-nums`}>
                            {amt >= 0 ? '+' : '−'}{usd(Math.abs(amt))}
                          </td>
                          <td className={td} style={{ color: MUTED }}>{KIND_STATUS[l.kind] || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <TableFooter
                shown={visible.length}
                total={moneyRows.length}
                hasMore={visible.length < moneyRows.length}
                onLoadMore={() => setShown((n) => n + PAGE)}
              />
            </>
          ) : (
            <div className="text-[12px]" style={{ color: MUTED }}>No history yet.</div>
          )}
        </>
      )}
    </Section>
  );
}
